/**
 * The Data (Bases) surface's command tree.
 *
 * **Why this subtree parses arguments differently from the rest of the CLI.**
 * WayAI's own commands are dispatched by the hand-rolled `switch` in `index.ts`,
 * each parsing its own `string[]`. The Data surface arrived as a large,
 * deeply-nested command tree (`bases seed lease status` is four levels), and
 * rewriting its argument parsing by hand would have been a rewrite rather than a
 * port — the class of change that silently drops a flag. So this subtree keeps a
 * declarative parser, and the DEPENDENCY is confined: `commander` is imported
 * nowhere outside `src/data/`. (The modules themselves are not isolated, nor
 * should they be — commands here reuse `lib/`'s auth, config and prompts, and
 * `lib/utils.ts` reads this surface's namespace list.)
 *
 * **The collision rule is enforced by the shape of the dispatch, not by care.**
 * `index.ts` consults `isDataNamespace` only in its `default:` branch, so every
 * WayAI `case` is matched first and a Data namespace can never shadow a WayAI
 * command even if someone adds a colliding word here
 * (docs/reference/rekor-platform-contracts.md §3.2; asserted in
 * `registry.test.ts`).
 */

import { Command } from 'commander';
import { buildActionsCommand } from './commands/actions.js';
import { buildAttachmentsCommand } from './commands/attachments.js';
import { buildBasesCommand } from './commands/bases.js';
import { buildFileTypesCommand } from './commands/file-types.js';
import { buildFilesCommand } from './commands/files.js';
import { buildInboundWebhooksCommand } from './commands/inbound-webhooks.js';
import { buildQueryRelationshipsCommand } from './commands/query-relationships.js';
import { buildRecordTypesCommand } from './commands/record-types.js';
import { buildRecordsCommand } from './commands/records.js';
import { buildRelationshipTypesCommand } from './commands/relationship-types.js';
import { buildRelationshipsCommand } from './commands/relationships.js';
import { buildSeedCommand } from './commands/seed.js';
import { buildToolsetsCommand } from './commands/toolsets.js';
import { buildTriggersCommand } from './commands/triggers.js';
import { globals, withBaseOption } from './helpers.js';
import { expected } from '../lib/expected.js';
import { setDataOrgOverride } from './org-context.js';
import { isDataNamespace } from './registry.js';

export { isDataNamespace, DATA_NAMESPACES } from './registry.js';

/**
 * Declare the options every Data command honors, on a NAMESPACE root rather than
 * on the program root.
 *
 * The program root is never invoked on its own — `index.ts` always enters at a
 * namespace — so options declared there parse correctly but appear in no help
 * output a user can reach, which is how `--json` ends up undiscoverable. On the
 * namespace they show under `wayai <namespace> --help` and still resolve for
 * nested subcommands, since commander looks options up through ancestors.
 */
export function withDataGlobals(command: Command): Command {
  return command
    .option('--org <uuid>', "Organization to operate against (overrides the repo's org binding)")
    .option('--output <format>', 'Output format: json or table', 'table')
    .option('--json', 'Shorthand for --output json');
}

/**
 * Add `--base` to a namespace whose commands operate against one. Defined in
 * `helpers.ts` (the command modules need it, and importing it from here would
 * close a cycle) and re-exported so the namespace builders below read as one
 * pipeline.
 */
export { withBaseOption } from './helpers.js';

/**
 * Hand every command in the tree to the CLI's one error path.
 *
 * Both settings below are per-command and NOT inherited by subcommands, so
 * applying them to the root alone would leave the leaves — where usage errors
 * actually happen — behaving differently from the root:
 *
 *  - `exitOverride()` turns an exit into a throw. Without it the parser calls
 *    `process.exit` directly, skipping secret masking, the Sentry flush, and the
 *    update nudge.
 *  - `outputError` is silenced because the parser writes the error message AND
 *    throws it, so leaving both writers on prints every usage error twice. The
 *    thrown error carries the same text, suggestions included.
 *
 * Only `outputError` is silenced, NOT `writeErr`. They are not
 * interchangeable: `writeErr` also carries the help text the parser renders when
 * a command group is invoked with no subcommand. Silencing it made `wayai bases`
 * print nothing at all — and since that path throws `commander.help`, whose
 * message is the literal string `(outputHelp)` rather than anything readable, it
 * also exited 0. A caller could not tell that from success.
 */
export function routeErrorsToCli(command: Command): Command {
  command.exitOverride().configureOutput({ outputError: () => {} });
  for (const child of command.commands) routeErrorsToCli(child);
  return command;
}

/**
 * Translate a commander throw into this CLI's error convention. Every entry
 * point into a Data tree routes its parse failures through here, so a second
 * entry point cannot get the discrimination subtly different.
 *
 * `exitOverride` turns THREE different outcomes into throws, and conflating the
 * last two is how `wayai bases` came to exit 0 with no output:
 *
 *  - **Requested help or version** (`--help`, `--version`) — a RESULT. Already
 *    rendered to stdout; return quietly, exit 0.
 *  - **Help rendered because the invocation was incomplete** (a command group
 *    with no subcommand) — a FAILURE that is already fully rendered to stderr.
 *    Its message is the placeholder `(outputHelp)`, so rethrowing would print
 *    that instead of anything useful. Set the exit code and return, which still
 *    runs the Sentry flush on the way out.
 *  - **A usage error** — rethrow TAGGED, so the CLI's handler prints the message
 *    once and `shouldReportToSentry` drops it. Untagged, every mistyped flag on
 *    a Data command becomes a Sentry event and a report in the autofix queue.
 *
 * Anything that is not a commander exit is a real crash and rethrows unchanged.
 *
 * Returns normally when the throw was already fully handled; throws otherwise.
 * A caller's `catch` therefore ends with this call and nothing after it.
 */
export function handleParserExit(err: unknown): void {
  const { code, exitCode } = (err ?? {}) as { code?: string; exitCode?: number };
  if (code === 'commander.helpDisplayed' || code === 'commander.version') return;
  if (code === 'commander.help') {
    process.exitCode = exitCode || 1;
    return;
  }
  if (typeof code === 'string' && code.startsWith('commander.')) {
    throw expected(err instanceof Error ? err.message : String(err));
  }
  throw err;
}

/**
 * Build the program rooted at `wayai`, carrying every Data namespace. Rebuilt
 * per invocation rather than kept module-level so a parse can never inherit
 * option state from a previous one (which matters in tests, where several
 * invocations share a process).
 */
export function buildDataProgram(): Command {
  const program = new Command('wayai');
  program.addCommand(withDataGlobals(buildBasesCommand()));

  // Every ontology namespace addresses rows INSIDE a base, so each takes
  // `--base`. `bases` above is the one that does not: it names its target
  // positionally.
  for (const build of [
    buildRecordsCommand,
    buildRecordTypesCommand,
    buildRelationshipsCommand,
    buildRelationshipTypesCommand,
    buildQueryRelationshipsCommand,
    buildFilesCommand,
    buildFileTypesCommand,
    buildAttachmentsCommand,
    buildToolsetsCommand,
    buildActionsCommand,
    buildTriggersCommand,
    buildInboundWebhooksCommand,
    buildSeedCommand,
  ]) {
    program.addCommand(withBaseOption(withDataGlobals(build())));
  }

  // Publish the resolved `--org` before any action runs, so no command has to
  // remember to thread it (see `org-context.ts` for why that matters).
  program.hook('preAction', (_thisCommand, actionCommand) => {
    setDataOrgOverride(globals(actionCommand).org);
  });

  return routeErrorsToCli(program);
}

/**
 * Run one Data-surface invocation. `namespace` is the top-level word `index.ts`
 * matched; `args` is everything after it.
 *
 * Commander's `exitOverride` turns `--help` and usage errors into throws so they
 * reach the CLI's one error path instead of calling `process.exit` from inside a
 * library; `handleParserExit` owns telling those outcomes apart. `admin bases`
 * enters the Data tree through its own program and shares that same handler.
 */
export async function runDataCommand(namespace: string, args: string[]): Promise<void> {
  if (!isDataNamespace(namespace)) {
    throw new Error(`Not a Data command: ${namespace}`);
  }
  const program = buildDataProgram();
  try {
    await program.parseAsync(['node', 'wayai', namespace, ...args]);
  } catch (err) {
    handleParserExit(err);
  }
}
